/**
 * Página de Login
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVertical } from '../context/VerticalContext';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../utils/analytics';
import Icon from '../components/Icon';
import './AuthPages.css';

const LOCKOUT_DURATION = 30; // segundos

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, login, loading: authLoading } = useAuth();
  const { isMedical } = useVertical();

  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('');

  // Rate limiting
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Countdown del lockout
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const timer = setTimeout(() => setLockoutSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [lockoutSeconds]);

  const getLockoutDuration = useCallback((attempts) => {
    if (attempts >= 10) return 300; // 5 minutos
    if (attempts >= 5) return LOCKOUT_DURATION;
    return 0;
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (lockoutSeconds > 0) return;

    setError('');
    setSuccessMessage('');

    if (!formData.email || !formData.password) {
      setError(t('login.errors.requiredFields'));
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        setFailedAttempts(0);
        trackEvent('login', { login_method: 'email' });
        navigate('/');
      } else if (result.requiresNewPassword) {
        navigate('/new-password');
      } else if (result.requiresConfirmation) {
        setUnconfirmedEmail(result.email || formData.email);
        setError(t('login.errors.unconfirmedAccount'));
      } else {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        const lockDuration = getLockoutDuration(newAttempts);
        if (lockDuration > 0) {
          setLockoutSeconds(lockDuration);
          setError(t('login.errors.tooManyAttempts', { seconds: lockDuration }));
        } else {
          setError(result.error || t('login.errors.loginError'));
        }
      }
    } catch (err) {
      console.error('Error en login:', err);
      setError(t('login.errors.loginError'));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          {/* Chrome de marca por vertical (NER-4111): mismo par icono+wordmark
              que el Header del panel, así el login /consultame/admin/login
              refleja Consultame en vez de Citame. */}
          <div className="auth-brand">
            <Icon
              name={isMedical ? 'medical_services' : 'event_available'}
              className="auth-brand-icon"
            />
            <h1>{isMedical ? 'Consultame' : 'Citame'}</h1>
          </div>
          <h2>{t('login.adminPanel')}</h2>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
              autoComplete="email"
              disabled={lockoutSeconds > 0}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              disabled={lockoutSeconds > 0}
            />
          </div>

          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
              {unconfirmedEmail && (
                <div style={{ marginTop: '8px' }}>
                  <Link to={`/register?resend=${encodeURIComponent(unconfirmedEmail)}`}>
                    {t('login.resendConfirmation')}
                  </Link>
                </div>
              )}
            </div>
          )}

          {successMessage && (
            <div className="confirmation-message" role="status" aria-live="polite">
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || lockoutSeconds > 0}
          >
            {loading
              ? t('login.signingIn')
              : lockoutSeconds > 0
                ? t('login.lockedOut', { seconds: lockoutSeconds })
                : t('login.signIn')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            <Link to="/forgot-password">{t('auth.forgotPassword')}</Link>
          </p>
          <p>
            {t('login.noAccount')} <Link to="/register">{t('login.registerHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
